import type { PERMISSIONS } from "@genius-tracker/api-client/permission-catalog";
import {
  ROLES,
  ROLE_PERMISSIONS as GENERATED_ROLE_PERMISSIONS,
} from "@genius-tracker/api-client/permission-catalog";

/**
 * Every catalogued permission, not only those a built-in role carries:
 * `tenant.members.read` exists for custom roles without being on a built-in one.
 */
export type Permission = (typeof PERMISSIONS)[number];

export interface Role {
  readonly id: string;
  readonly label: string;
  readonly permissions: readonly Permission[];
}

/**
 * Read-only display data derived from the generated API catalog.
 *
 * This is deliberately not an authorization source: access decisions belong
 * in `lib/access.ts`, which reads the generated catalog directly. Consumers
 * may render this table but must not use it to grant or deny access.
 */
export const BUILT_IN_ROLES: Readonly<Record<string, Role>> = Object.fromEntries(
  ROLES.map((role) => [role.id, role]),
);

export type BuiltInRoleId = keyof typeof BUILT_IN_ROLES;

/** Platform roles intentionally remain outside the tenant role catalog. */
export const PLATFORM_UI_ROLES = ["platform.owner", "platform.admin"] as const;

export function isPlatformUiRole(roleId: string): boolean {
  return (PLATFORM_UI_ROLES as readonly string[]).includes(roleId);
}

export const MEMBER_ROLE_TO_BUILTIN_ID = {
  owner: "tenant.owner",
  admin: "tenant.admin",
  dispatcher: "dispatcher",
  viewer: null,
} as const satisfies Record<string, BuiltInRoleId | null>;

export type MemberRoleTag = keyof typeof MEMBER_ROLE_TO_BUILTIN_ID;

export function permissionsForMemberRole(tag: string): readonly Permission[] {
  const builtIn = (MEMBER_ROLE_TO_BUILTIN_ID as Record<string, BuiltInRoleId | null | undefined>)[
    tag
  ];
  return builtIn ? permissionsForRole(builtIn) : [];
}

export function permissionDescKey(permission: Permission): string {
  return `permission.desc.${permission}`;
}

export function roleLabelKey(roleId: string): string {
  return `admin.roles.label.${roleId}`;
}

export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  "audit.read": "View the audit log.",
  "billing.admin": "Manage billing details and invoice settings.",
  "billing.read": "View billing summary and invoices.",
  "device.read": "View device inventory and status.",
  "device.write": "Provision or update devices.",
  "dispatch.read": "View dispatch boards and assignments.",
  "dispatch.write": "Assign work and manage dispatch state.",
  "driver.field_mode": "Use the driver field mode surface (driver self-scope).",
  "driver.read": "View driver records.",
  "driver.write": "Create or update driver records.",
  "fleet.admin": "Reorganise fleet hierarchy, depots, and tags.",
  "fleet.read": "View fleet roster and dashboards.",
  "fleet.write": "Create or update vehicles and fleet metadata.",
  "fuel.statement.self.read":
    "View one's own fuel review cases and statements (driver self-scope).",
  "fuel.statement.self.write": "Answer a fuel review case about oneself (driver self-scope).",
  "geofence.read": "View geofences and membership state.",
  "geofence.write": "Create or update geofences.",
  "platform.sims.manage": "Manage the platform SIM inventory (Platform Owner only).",
  "platform.tenants.read": "Read cross-tenant tenant records (Platform Owner only).",
  "platform.tenants.write": "Create, edit, and lifecycle-transition tenants (Platform Owner only).",
  "reporting.read": "View reports and exports.",
  "telemetry.read": "View live and historical telemetry.",
  "telemetry.write": "Ingest telemetry (machine-only role).",
  "tenant.admin": "Configure tenant-level settings and entitlements.",
  "tenant.members.read": "View tenant member records.",
  "tenant.members.write": "Invite, suspend, and reactivate tenant members.",
  "tenant.read": "View tenant metadata and member list.",
  "trip.read": "View trip records.",
  "vehicle.read": "View individual vehicle records.",
  "vehicle.write": "Create or update vehicle records.",
  "visibility.read": "View user groups, visibility grants and previews.",
  "visibility.write": "Manage user groups, visibility grants and the visibility mode.",
};

export function permissionsForRole(roleId: string): readonly Permission[] {
  return GENERATED_ROLE_PERMISSIONS[roleId] ?? [];
}

export function permissionsFromRoles(roleIds: readonly string[]): readonly Permission[] {
  return Array.from(new Set(roleIds.flatMap(permissionsForRole))).sort();
}
